import { AccountCircleOutlined, Event, PrecisionManufacturing, RequestQuoteOutlined, TipsAndUpdatesOutlined, TrackChanges } from '@mui/icons-material';
import { Alert, AlertTitle, List, ListItem, ListItemIcon, ListItemText, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { Card } from "randmarcomps";
import { inject, observer } from "mobx-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { Requisition } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import RequisitionPartNumberRow from "./RequisitionPartNumberRow";

interface IProps {
  store?: RootStore;
  requisition: Requisition;
}

const RequisitionView: React.FC<IProps> = ({ requisition, store }) => {
  const { warehousesStore } = store!;

  const sortedPartNumbers = React.useMemo(() => {
    return (requisition.partNumbers || [])
      .slice()
      .sort((a, b) => (b.mpn || "").localeCompare(a.mpn || ""));
  }, [requisition.partNumbers]);

  return (
    <>
        <Card className="w-full mb-4">
          <div style={{ padding: "15px" }}>
            <h4 style={{ marginBottom: 5, margin: "20px", marginTop: "10px" }}>Draft Purchase Order Information</h4>
            <Alert severity="info" sx={{ m: 3, mt: 0 }}>
              <AlertTitle color="#3b6cca">Draft Purchase Editing</AlertTitle>
              You can modify the draft through the actions button at the top right.
            </Alert>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="flex-end" alignItems="center" sx={{ m: 2 }} spacing={1}>
              <List dense disablePadding sx={{ width: '100%', bgcolor: 'background.paper' }}>
                <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                  <ListItemIcon><Event /></ListItemIcon>
                  <ListItemText primary="Draft Purchase Date" secondary={FormatDateFromInt(requisition.requisitionDate)} />
                </ListItem>
                <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                  <ListItemIcon><AccountCircleOutlined /></ListItemIcon>
                  <ListItemText primary="User" secondary={requisition.requestedBy} />
                </ListItem>
                <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                  <ListItemIcon><TipsAndUpdatesOutlined /></ListItemIcon>
                  <ListItemText primary="Reference" secondary={requisition.reference === "" ? "None" : requisition.reference} />
                </ListItem>
              </List>
              <List dense disablePadding sx={{ width: '100%', bgcolor: 'background.paper' }}>
                <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                  <ListItemIcon><PrecisionManufacturing /></ListItemIcon>
                  <ListItemText primary="Location" secondary={warehousesStore.getWarehouseNameFromCode(requisition.location)} />
                </ListItem>
                <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                  <ListItemIcon><RequestQuoteOutlined /></ListItemIcon>
                  <ListItemText primary="Total" secondary={<FormattedNumber {...CurrencyUtils.format} value={requisition.subTotal || 0} />} />
                </ListItem>
                <ListItem sx={{ "&:hover": { backgroundColor: "#f9f5ff" }, borderRadius: "20px" }}>
                  <ListItemIcon><TrackChanges /></ListItemIcon>
                  <ListItemText primary="Total Cost"
                    secondary={requisition.partNumbers ?
                      <FormattedNumber value={requisition.partNumbers.reduce((a, b) => a + ((b.unitPrice || 0) * (b.quantityOrdered || 0)), 0)} {...CurrencyUtils.format} />
                      :
                      "Undefined"} />
                </ListItem>
              </List>
            </Stack>
          </div>
        </Card>
        <Card className="w-full mb-4">
          <div style={{ padding: "15px" }}>
            <h4 style={{ marginBottom: 5, margin: "20px", marginTop: "10px" }}>Part Numbers ({sortedPartNumbers.length})</h4>
            <div style={{ borderRadius: '20px', width: '100%', overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f5ff" }}>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Product</TableCell>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Quantity</TableCell>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }} />
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Unit Price</TableCell>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Extended Price</TableCell>
                    <TableCell sx={{ borderColor: "#582c83" }} />
                    <TableCell sx={{ borderColor: "#582c83" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPartNumbers.map((row, index) => {
                      return (
                        <RequisitionPartNumberRow
                          key={"RequisitionPartNumber" + row.mpn + "Index" + index}
                          index={index}
                          partNumber={row}
                          requisitionNumber={requisition.requisitionNumber || ""} />
                      );
                    }
                    )}
                </TableBody>
              </Table>
            </div>
          </div>
        </Card>
      </>
    );
};

export default inject("store")(observer(RequisitionView));
